<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'

const props = defineProps<{
  powerLevel: number
  isCharging: boolean
}>()

const emit = defineEmits<{
  (e: 'start-charge'): void
  (e: 'release'): void
}>()

// 显示引导提示
const showTutorial = ref(true)
const hideTutorial = () => {
  showTutorial.value = false
  localStorage.setItem('toilet-tutorial-shown', 'true')
}

// 计算蓄力颜色
const powerColor = computed(() => {
  if (props.powerLevel < 30) return '#5eff7a'
  if (props.powerLevel < 70) return '#fff95e'
  return '#ff5e5e'
})

// 计算蓄力文字
const powerText = computed(() => {
  if (props.powerLevel < 30) return '弱'
  if (props.powerLevel < 70) return '中'
  return '强'
})

// 计算肛门动画状态
const sphincterState = computed(() => {
  if (!props.isCharging) return 'idle'
  if (props.powerLevel < 30) return 'low'
  if (props.powerLevel < 70) return 'medium'
  return 'high'
})

// 开始蓄力
const startCharge = () => {
  if (!props.isCharging) {
    emit('start-charge')
    hideTutorial()
  }
}

// 释放
const release = () => {
  if (props.isCharging) {
    emit('release')
  }
}

// 组件挂载时检查是否显示教程
onMounted(() => {
  if (localStorage.getItem('toilet-tutorial-shown')) {
    showTutorial.value = false
  }
})
</script>

<template>
  <div class="power-meter">
    <!-- 蓄力引导提示 -->
    <div class="tutorial-tip" v-if="showTutorial">
      <div class="tutorial-arrow"></div>
      <div class="tutorial-text">长按按钮蓄力</div>
    </div>

    <div
      class="sphincter"
      :class="[`sphincter-${sphincterState}`]"
      @mousedown="startCharge"
      @mouseup="release"
      @touchstart.prevent="startCharge"
      @touchend.prevent="release"
    >
      <div class="sphincter-inner"></div>
    </div>

    <div class="power-bar">
      <div class="power-gauge">
        <div
          class="power-fill"
          :style="{
            height: `${powerLevel}%`,
            backgroundColor: powerColor,
          }"
        ></div>
      </div>
      <div class="power-indicator" :style="{ color: powerColor }">{{ powerText }}</div>
    </div>
  </div>
</template>

<style scoped>
.power-meter {
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 10;
}

.sphincter {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: #ffc0cb;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
  cursor: pointer;
  transition: transform 0.3s ease;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
  position: relative;
}

.sphincter::after {
  content: '';
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: transparent;
  border: 2px solid white;
  opacity: 0;
  animation: pulse 2s infinite;
}

.sphincter-inner {
  width: 30%;
  height: 30%;
  background: #ff6b81;
  border-radius: 50%;
  transition: all 0.3s ease;
}

.sphincter-idle .sphincter-inner {
  width: 30%;
  height: 30%;
}

.sphincter-low .sphincter-inner {
  width: 25%;
  height: 25%;
  animation: pulseLow 0.5s infinite;
}

.sphincter-medium .sphincter-inner {
  width: 20%;
  height: 20%;
  animation: pulseMedium 0.3s infinite;
}

.sphincter-high .sphincter-inner {
  width: 10%;
  height: 10%;
  animation: pulseHigh 0.1s infinite;
}

.power-bar {
  width: 30px;
  height: 150px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 15px;
  padding: 5px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
  position: relative;
}

.power-gauge {
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 10px;
  overflow: hidden;
  position: relative;
}

.power-fill {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  transition: height 0.1s ease-out;
}

.power-indicator {
  position: absolute;
  right: -25px;
  top: 50%;
  transform: translateY(-50%);
  font-weight: bold;
  text-shadow: 0 0 3px rgba(0, 0, 0, 0.5);
}

.tutorial-tip {
  position: absolute;
  bottom: 110%;
  background: rgba(255, 255, 255, 0.9);
  color: #333;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 14px;
  white-space: nowrap;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  animation: bounce 2s infinite;
}

.tutorial-arrow {
  position: absolute;
  bottom: -10px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  border-top: 10px solid rgba(255, 255, 255, 0.9);
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 0.6;
  }
  70% {
    transform: scale(1.1);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 0;
  }
}

@keyframes bounce {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-5px);
  }
}

@keyframes pulseLow {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(0.9);
  }
  100% {
    transform: scale(1);
  }
}

@keyframes pulseMedium {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(0.7);
  }
  100% {
    transform: scale(1);
  }
}

@keyframes pulseHigh {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(0.5);
  }
  100% {
    transform: scale(1);
  }
}
</style>
